import EditIcon from '@mui/icons-material/Edit'
import MailIcon from '@mui/icons-material/Mail'
import PhoneIcon from '@mui/icons-material/Phone'
import { TabContext, TabPanel } from '@mui/lab'
import TabList from '@mui/lab/TabList'
import { Chip, Divider, IconButton, Typography } from '@mui/material'
import { GetCompanyQuery } from '__generated__/graphql'
import React, { Fragment, useState } from 'react'
import { ContactGroups } from 'recruiter/types/ContactGroups'
import useIsMobile from 'shared/hooks/useIsMobile'
import { getPxAsRem } from 'shared/utils'
import EditClientContacts from '../EditClientContacts'
import {
    ContactDetails,
    ContactEmailPhone,
    ContactRow,
    Header,
    IconContainer,
    InfoText,
    MobileContactRow,
    StyledCard,
    StyledTab,
} from './styled'

interface ClientContactsProps {
    data: GetCompanyQuery
    refetch: () => void
}

const ContactCard = ({ contact, contactType }) => {
    const firstName =
        contact.candidate?.user?.nickname ||
        contact.candidate?.user?.firstName ||
        '-'
    const lastName = contact.candidate?.user?.lastName || '-'
    const email = contact.candidate?.user?.email || '-'
    const rawPhone = contact.candidate?.user?.phone || '-'
    const phone = rawPhone.startsWith('+1')
        ? rawPhone.slice(2).replace(/(\d{3})(\d{3})(\d{4})/, '$1.$2.$3') // format US phone number
        : rawPhone
    const location =
        contact.candidate?.currentWorkExperience?.companyOffice?.officeLocation
            ?.value || '-'
    const industry =
        contact.candidate?.currentWorkExperience?.industry?.value || '-'

    return (
        <>
            <ContactRow key={contact.candidateId}>
                <ContactDetails>
                    <InfoText
                        variant="body2"
                        title={`${firstName} ${lastName}`}
                    >{`${firstName} ${lastName}`}</InfoText>
                    <Chip label={contactType} size="small" sx={{ mx: 1 }} />
                    <InfoText variant="body2" title={location}>
                        {location}
                    </InfoText>
                </ContactDetails>
                <ContactEmailPhone>
                    <InfoText variant="body2" title={email}>
                        {email}
                    </InfoText>
                    <InfoText variant="body2" title={phone}>
                        {phone}
                    </InfoText>
                    <InfoText variant="body2" title={industry}>
                        {industry}
                    </InfoText>
                </ContactEmailPhone>
            </ContactRow>
        </>
    )
}

const MobileContactCard = ({ contact, contactType }) => {
    const firstName = contact.candidate?.user?.firstName || '-'
    const lastName = contact.candidate?.user?.lastName || '-'

    return (
        <MobileContactRow key={contact.candidateId}>
            <Typography variant="body1">{`${firstName} ${lastName}`}</Typography>
            <IconContainer>
                <MailIcon color="primary" />
                <PhoneIcon color="primary" />
            </IconContainer>
            <Chip label={contactType} size="small" sx={{ mx: 1 }} />
        </MobileContactRow>
    )
}

const renderContacts = (contacts, group: ContactGroups, isMobile: boolean) => {
    const filteredContacts =
        contacts?.filter((contact) => contact.group === group) || []
    if (filteredContacts.length === 0) {
        return (
            <Typography
                variant="body1"
                textAlign="center"
                sx={{ padding: getPxAsRem(28) }}
            >
                No contacts available.
            </Typography>
        )
    }

    return filteredContacts.map((contact, index) => {
        const contactType = contact.contactType || '-'
        return isMobile ? (
            <React.Fragment key={contact.candidateId}>
                <MobileContactCard
                    contact={contact}
                    contactType={contactType}
                />
                {index !== filteredContacts.length - 1 && <Divider />}
            </React.Fragment>
        ) : (
            <React.Fragment key={contact.candidateId}>
                <ContactCard contact={contact} contactType={contactType} />
                {index !== filteredContacts.length - 1 && <Divider />}
            </React.Fragment>
        )
    })
}

const ClientContacts: React.FC<ClientContactsProps> = ({ data, refetch }) => {
    const [selectedTab, setSelectedTab] = useState('Pre-MBA')
    const isMobile = useIsMobile()
    const handleChange = (event: React.SyntheticEvent, newValue: string) =>
        setSelectedTab(newValue)
    const [editContact, setEditContact] = React.useState(false)

    return (
        <Fragment>
            <EditClientContacts
                open={editContact}
                onClose={() => {
                    setEditContact(!editContact)
                    refetch()
                }}
            />
            <StyledCard>
                <Header>
                    <Typography variant="h4">Client Contacts</Typography>
                    {!isMobile && (
                        <IconButton
                            size="small"
                            onClick={() => setEditContact(true)}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    )}
                </Header>

                <TabContext value={selectedTab}>
                    <TabList
                        onChange={handleChange}
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        {Object.values(ContactGroups).map((group) => (
                            <StyledTab
                                key={group}
                                label={group}
                                value={group}
                            />
                        ))}
                    </TabList>
                    <Divider />

                    {Object.values(ContactGroups).map((group) => (
                        <TabPanel key={group} value={group} sx={{ padding: 0 }}>
                            {renderContacts(
                                data?.company?.clientContacts,
                                group,
                                isMobile
                            )}
                        </TabPanel>
                    ))}
                </TabContext>
            </StyledCard>
        </Fragment>
    )
}

export default ClientContacts
